import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { formatEther } from 'viem'
import { formatFormAmount } from '~/utils/numbers'
import { RequestStatusPending } from './types'

interface RequestPendingItemProps {
  request: RequestStatusPending
}

const formatFinalizationTime = (finalizationAt: string | null) => {
  const { t } = useTranslation()

  if (!finalizationAt) return '~'

  const finalizationTime = new Date(finalizationAt).getTime()
  const now = Date.now()
  const diffMs = finalizationTime - now

  if (diffMs <= 0) return '~'

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  return diffDays === 1 ? `1 ${t('day')}` : `${diffDays} ${t('days')}`
}

const RequestPendingItem: React.FC<RequestPendingItemProps> = ({ request }) => {
  const { t } = useTranslation()
  const formattedAmount = formatFormAmount(formatEther(request.amountOfStETH))
  const formattedFinalizationTime = formatFinalizationTime(
    request.finalizationAt,
  )

  return (
    <StyledRequestItem>
      <StyledAmount>{formattedAmount} stETH</StyledAmount>
      <StyledStatusWrapper>
        <StyledStatus>{t('claim_status_pending')}</StyledStatus>
        <StyledCompletionTimeWrapper>
          <Trans
            i18nKey="claim_completed_in"
            values={{ days: formattedFinalizationTime }}
            components={{
              special: (
                <StyledCompletionTime>
                  {formattedFinalizationTime}
                </StyledCompletionTime>
              ),
            }}
          />
        </StyledCompletionTimeWrapper>
      </StyledStatusWrapper>
    </StyledRequestItem>
  )
}

const StyledRequestItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 63px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgSurface};
  margin-bottom: 8px;
  opacity: 0.8;

  &:last-child {
    margin-bottom: 0;
  }
`

const StyledAmount = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textTitle};
`

const StyledStatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const StyledCompletionTimeWrapper = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  color: ${({ theme }) => theme.colors.textCaption};
`

const StyledCompletionTime = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`

const StyledStatus = styled.div`
  padding: 3px 8px;
  border: 1px solid ${({ theme }) => theme.colors.statusWarning};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.statusWarning};
  background: ${({ theme }) => theme.colors.statusWarningBg};
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`

export default RequestPendingItem
